import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ActivityIcon, BanIcon, BuildingIcon, ShieldCheckIcon, UsersIcon } from "lucide-react";
import { platformStatsQuery } from "@/core/lib/admin-api";
import { CardSkeleton, skeletonKeys } from "@/ui/components/skeletons/page-skeletons";
import { Skeleton } from "@/ui/components/ui/skeleton";

/**
 * The admin Overview. Every staff account may see this, so unlike the section
 * routes it carries no `requireAdminSection` guard — the `_admin` layout's
 * check is the whole gate, and `admin-guard` bounces here rather than away.
 *
 * The counters are loaded in the route loader so the cards render with real
 * numbers on first paint, which is what the server render used to buy us.
 * `loading.tsx` becomes `pendingComponent`.
 */
export const Route = createFileRoute("/_admin/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(platformStatsQuery),
  component: AdminDashboardPage,
  pendingComponent: AdminDashboardLoading,
});

const fmt = (n: number | undefined) => (n === undefined ? "—" : n.toLocaleString());

function AdminDashboardPage() {
  const { data: stats } = useSuspenseQuery(platformStatsQuery);

  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          <ShieldCheckIcon className="size-3.5" />
          Internal admin
        </div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Admin dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Operate HR Docket — monitor companies, support customers, and review system health.
        </p>
      </header>
      <div className="grid auto-rows-min gap-4 md:grid-cols-2 xl:grid-cols-4">
        <AdminStatCard
          label="Users"
          value={fmt(stats?.totalUsers)}
          hint="Total registered accounts"
          icon={UsersIcon}
        />
        <AdminStatCard
          label="Companies"
          value={fmt(stats?.totalCompanies)}
          hint="Total registered companies"
          icon={BuildingIcon}
        />
        <AdminStatCard
          label="Active sessions"
          value={fmt(stats?.activeSessions)}
          hint="Currently signed in"
          icon={ActivityIcon}
        />
        <AdminStatCard
          label="Banned users"
          value={fmt(stats?.bannedUsers)}
          hint="Accounts under suspension"
          icon={BanIcon}
        />
      </div>
      {stats === null ? (
        <div className="flex min-h-96 flex-1 items-center justify-center rounded-lg border border-dashed bg-muted/30 text-sm text-muted-foreground">
          Couldn't reach the backend to load platform stats.
        </div>
      ) : null}
    </div>
  );
}

function AdminStatCard({
  label,
  value,
  hint,
  icon: Icon,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border bg-card p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className="flex size-7 items-center justify-center rounded-md bg-muted text-muted-foreground">
          <Icon className="size-3.5" />
        </span>
      </div>
      <div className="text-3xl font-semibold tracking-tight">{value}</div>
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function AdminDashboardLoading() {
  return (
    <div className="flex flex-1 flex-col gap-6 p-6">
      <header className="flex flex-col gap-1.5">
        <Skeleton className="h-3 w-28 rounded-md" />
        <Skeleton className="h-8 w-64 rounded-md" />
        <Skeleton className="h-4 w-full max-w-lg rounded-md" />
      </header>
      <div className="grid auto-rows-min gap-4 md:grid-cols-2 xl:grid-cols-4">
        {skeletonKeys(4, "stat").map((key) => (
          <CardSkeleton key={key} />
        ))}
      </div>
    </div>
  );
}
